import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { OrganisationalUnit } from '@teammood/shared/util/core';
import { Team, TeamDialogService, TeamService } from '@teammood/team';
import { UserService, UserTeam } from 'app/frontend/lib/shared/util/user';
import { OrganisationalUnitNamesByType, SetupService } from '../setup.service';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { LoggerService } from "@teammood/shared/util/logger";
import { catchError, EMPTY, filter, from, mergeMap, ReplaySubject, Subject, takeUntil, tap } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";

@Component({
    selector: 'team-setup',
    templateUrl: './team-setup.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TeamSetupComponent implements OnInit, OnDestroy {

    protected team: Team;

    orgUnits: ViewOrganisationalUnit[];
    teamId: string;
    availableOrgUnits: OrganisationalUnitNamesByType[];
    availableOrgUnitNames: string[];
    editName: boolean = false;
    createMode: boolean = false;

    teams: Subject<UserTeam[]> = new ReplaySubject<UserTeam[]>();

    private _destroy$ = new Subject<void>();

    constructor(
        private log: LoggerService,
        private userService: UserService,
        private teamService: TeamService,
        private teamDialogService: TeamDialogService,
        private setupService: SetupService,
        private snackBarService: SnackBarService,
        private route: ActivatedRoute,
        private router: Router,
    ) {}

    async ngOnInit() {

        this.route.params.pipe(
            filter(params => !!params['teamId']),
            mergeMap(params => {
                this.teamId = params['teamId'];
                return this.teamService.get(this.teamId);
            }),
            tap(team => {
                this.team = team;
                this.orgUnits = this.team.orgUnits.map(ou => (new ViewOrganisationalUnit(ou, false)))
            }),
            takeUntil(this._destroy$)
        ).subscribe();

        this.userService.get().then(user => {
            this.teams.next(user.teams);
        });

        this.availableOrgUnits = await this.setupService.getAvailableOrganisationalUnits();
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    createOrgUnit(){
        this.createMode = true;
    }

    async onCreateOrgUnit(newOrgUnit: OrganisationalUnit) {
        this.createMode = false;
        if(newOrgUnit === null) {
            return; // cancel
        }
       
        this.orgUnits = (await this.setupService.createOrganisationalUnit(newOrgUnit, this.team.id)).map(ou => (new ViewOrganisationalUnit(ou, false)));
        this.snackBarService.success('teamUpdated'); 
    }

    updateOrgUnit(orgUnit: ViewOrganisationalUnit) {
        this.availableOrgUnitNames = this.setupService.findAllOrgUnitNames(this.availableOrgUnits, orgUnit.ou.type);
        orgUnit.updateMode = true;
    }

    async onUpdateOrgUnit(orgUnit: OrganisationalUnit | undefined) {
        const updateIndex = this.orgUnits.findIndex(ou => ou.updateMode);
        this.orgUnits[updateIndex].updateMode = false;

        if(orgUnit === null) {
            return; // cancel
        }
        this.orgUnits = (await this.setupService.updateOrganisationalUnit(orgUnit, this.team.id)).map(ou => (new ViewOrganisationalUnit(ou, false)));
        this.availableOrgUnits = await this.setupService.getAvailableOrganisationalUnits();
        this.snackBarService.success('teamUpdated'); 
    }

    saveTeamName() {
        if (this.team) {
            this.teamService.update(this.team)
            .then(() => {
                this.userService.get(true).then(async (user)=>{
                    this.teams.next(user.teams);
                    this.snackBarService.success('teamUpdated');
                    this.editName = false;
                });

            })
            .catch(error => {
                this.log.error('Error on team name update:', error);
                this.snackBarService.error('Error saving the team name: ' + error);
            });
        }
    }
    
    removeOrgUnit(orgUnit: OrganisationalUnit){
        if (this.team){
            this.setupService.deleteOrganisationalUnit(orgUnit, this.team.id).then(data => {
                this.orgUnits = data.map(ou => (new ViewOrganisationalUnit(ou, false)));
                this.snackBarService.success('teamUpdated');
            })
        }
    }

    async openNewTeamDialog() {
        if(this.teamId) {
            const teamTemplate = await this.teamService.get(this.teamId);
            teamTemplate['name'] = "";
            teamTemplate['id'] = null;

            const dialogRef = await this.teamDialogService.newTeam(teamTemplate, this.teamId);

            dialogRef.beforeClosed().pipe(
                filter((createdTeam) => !!createdTeam),
                mergeMap((createdTeam) => {
                    this.teamId = createdTeam.id;
                    this.snackBarService.success("Successfully created team '" + createdTeam.name + "'");
                    return from(this.userService.get(true).then( user => {
                        this.teams.next(user.teams);

                        return this.router.navigate(['/setup/team', this.teamId]);
                    }));
                }),
                takeUntil(dialogRef.afterClosed())
            ).subscribe()

        }
    }

    async deleteTeam(team: Team) {
        const deleteDialogRef = await this.teamDialogService.delete(team);
    
        deleteDialogRef.beforeClosed().pipe(
          mergeMap((shouldDelete) => {
            if (shouldDelete) {
              return this.teamService.delete(this.team.id)
            } else {
              return EMPTY;
            }
          }),
          tap(() => {
            this.snackBarService.success("Successfully deleted team '" + team.name + "'");
            this.userService.get(true).then(async (user)=>{
                this.teams.next(user.teams);
                const team = user.teams[0];
                await this.router.navigate(['/setup/team', team.id]);
            });
          }),
          catchError((err) => {
            this.snackBarService.error(JSON.stringify(err));
            throw err;
          })).subscribe()
    }

}

class ViewOrganisationalUnit {
    ou: OrganisationalUnit;
    updateMode: boolean;

    constructor(organisationUnit: OrganisationalUnit, updateMode: boolean) {
        this.ou = organisationUnit;
        this.updateMode = updateMode;
    }
}